import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, Send, Check, Loader2, User, Bot, Mic, MicOff, Database, BookOpen, FileText, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useBrowserSpeechToText } from "@/hooks/useBrowserSpeechToText";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SUPABASE_CONFIG, SUPABASE_FUNCTIONS_URL } from "@/lib/supabaseConfig";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface EmailAttachment {
  name?: string;
  filename?: string;
  size?: number;
  contentType?: string;
  content?: string; // Base64 or text content
  partNumber?: string;
}

interface CustomerContext {
  firstName?: string;
  lastName?: string;
  customerNumber?: string;
  phone?: string;
  address?: string;
  notes?: string;
}

interface KnowledgeBaseEntry {
  title: string;
  content: string;
}

interface AIComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: (emailText: string, suggestedSubject?: string) => void;
  originalEmail?: {
    from?: string;
    subject?: string;
    body?: string;
    attachments?: EmailAttachment[];
    emailId?: string; // ID of the original email for attachment loading
  } | null;
  recipientEmail?: string; // Email address of the recipient (for new emails)
  userName?: string;
  signatureText?: string;
  currentSubject?: string;
  teamId?: string;
  organizationId?: string;
}

export const AIComposeDialog = ({
  open,
  onOpenChange,
  onAccept,
  originalEmail,
  recipientEmail,
  userName,
  signatureText,
  currentSubject,
  teamId,
  organizationId,
}: AIComposeDialogProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState("");
  const [suggestedSubject, setSuggestedSubject] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Context states
  const [customerContext, setCustomerContext] = useState<CustomerContext | null>(null);
  const [knowledgeBaseContent, setKnowledgeBaseContent] = useState<KnowledgeBaseEntry[]>([]);
  const [attachmentContent, setAttachmentContent] = useState<{ name: string; content: string }[]>([]);
  const [loadingContext, setLoadingContext] = useState(false);
  
  const {
    isRecording,
    isSupported: isSpeechSupported,
    interimTranscript,
    toggleRecording,
  } = useBrowserSpeechToText((text) => {
    // Update input with transcribed text in real-time
    setInput(text);
  });

  // Load context when dialog opens
  useEffect(() => {
    if (open && user) {
      loadContext();
    }
  }, [open, user, originalEmail?.from, recipientEmail]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInput("");
      setGeneratedEmail("");
      setSuggestedSubject(null);
      setCustomerContext(null);
      setKnowledgeBaseContent([]);
      setAttachmentContent([]);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, generatedEmail]);

  const loadContext = async () => {
    if (!user) return;
    
    setLoadingContext(true);
    
    try {
      // Determine email to look up customer - either from original email (reply) or recipient (new email)
      let customerEmail = originalEmail?.from || recipientEmail;
      
      // Extract email address (handles "Name <email>" format)
      if (customerEmail) {
        const emailMatch = customerEmail.match(/<([^>]+)>/);
        if (emailMatch) {
          customerEmail = emailMatch[1];
        }
        customerEmail = customerEmail.trim().toLowerCase();
      }

      // Load customer by email if we have one
      if (customerEmail) {
        const { data: customerData } = await supabase
          .from('customers')
          .select('first_name, last_name, customer_number, phone, address, notes')
          .eq('user_id', user.id)
          .ilike('email', customerEmail)
          .maybeSingle();

        if (customerData) {
          setCustomerContext({
            firstName: customerData.first_name,
            lastName: customerData.last_name || undefined,
            customerNumber: customerData.customer_number || undefined,
            phone: customerData.phone || undefined,
            address: customerData.address || undefined,
            notes: customerData.notes || undefined,
          });
          console.log('Found customer:', customerData.first_name, customerData.last_name);
        }
      }

      // Load knowledge base entries for user's team
      let userTeamId = teamId;
      
      if (!userTeamId) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('team_id')
          .eq('user_id', user.id)
          .single();
        
        userTeamId = profileData?.team_id || undefined;
      }

      if (userTeamId) {
        const { data: kbData } = await supabase
          .from('team_knowledge_base')
          .select('title, content')
          .eq('team_id', userTeamId)
          .limit(10); // Limit to prevent token overflow

        if (kbData && kbData.length > 0) {
          // Truncate content to prevent huge prompts
          const truncatedEntries = kbData.map(entry => ({
            title: entry.title,
            content: entry.content.length > 2000 
              ? entry.content.substring(0, 2000) + '...' 
              : entry.content
          }));
          setKnowledgeBaseContent(truncatedEntries);
          console.log('Loaded knowledge base entries:', truncatedEntries.length);
        }
      }
      // Load attachment content if attachments exist - extract text from PDFs and other files
      if (originalEmail?.attachments && originalEmail.attachments.length > 0) {
        const loadedAttachments: { name: string; content: string }[] = [];
        
        for (const attachment of originalEmail.attachments) {
          const filename = attachment.name || attachment.filename || 'unknown';
          const contentType = attachment.contentType || '';
          const lowerFilename = filename.toLowerCase();
          
          // If attachment already has text content loaded
          if (attachment.content && typeof attachment.content === 'string' && attachment.content.length > 100) {
            loadedAttachments.push({
              name: filename,
              content: attachment.content.length > 10000 
                ? attachment.content.substring(0, 10000) + '...' 
                : attachment.content
            });
            continue;
          }
          
          // Check if this is a file type we can extract text from
          const canExtractText = contentType.includes('pdf') || 
                                lowerFilename.endsWith('.pdf') ||
                                contentType.includes('text') || 
                                lowerFilename.endsWith('.txt') || 
                                lowerFilename.endsWith('.csv') ||
                                lowerFilename.endsWith('.json') ||
                                lowerFilename.endsWith('.xml') ||
                                lowerFilename.endsWith('.html') ||
                                lowerFilename.endsWith('.htm') ||
                                lowerFilename.endsWith('.md');
          
          if (canExtractText && (originalEmail as any).emailId) {
            // Try to extract text content via edge function
            try {
              console.log(`📄 Extracting text from attachment: ${filename}`);
              const { data: extractedData, error: extractError } = await supabase.functions.invoke(
                'fetch-email-attachment',
                {
                  body: {
                    emailId: (originalEmail as any).emailId,
                    attachmentIndex: originalEmail.attachments!.indexOf(attachment),
                    filename: filename,
                    partNumber: attachment.partNumber,
                    extractText: true
                  }
                }
              );
              
              if (!extractError && extractedData?.extractedText) {
                console.log(`✅ Extracted ${extractedData.extractedText.length} chars from ${filename}`);
                loadedAttachments.push({
                  name: filename,
                  content: extractedData.extractedText.length > 10000 
                    ? extractedData.extractedText.substring(0, 10000) + '...' 
                    : extractedData.extractedText
                });
                continue;
              } else if (extractError) {
                console.warn(`⚠️ Could not extract text from ${filename}:`, extractError);
              }
            } catch (err) {
              console.warn(`⚠️ Error extracting attachment text:`, err);
            }
          }
          
          // Fallback: just note the attachment presence
          const fileInfo = `[Anhang: ${filename}`;
          const sizeInfo = attachment.size ? `, ${Math.round(attachment.size / 1024)} KB` : '';
          const typeInfo = contentType ? `, ${contentType}` : '';
          loadedAttachments.push({
            name: filename,
            content: `${fileInfo}${sizeInfo}${typeInfo}]`
          });
        }
        
        if (loadedAttachments.length > 0) {
          setAttachmentContent(loadedAttachments);
          console.log('Loaded attachment content:', loadedAttachments.length, 'files');
        }
      }
    } catch (error) {
      console.error('Error loading AI context:', error);
    } finally {
      setLoadingContext(false);
    }
  };

  const streamChat = async (userMessage: string) => {
    setIsLoading(true);
    const newMessages: Message[] = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setInput("");

    let assistantContent = "";
    const needsSubject = !currentSubject || currentSubject.trim() === "";

    try {
      const response = await fetch(
        `${SUPABASE_FUNCTIONS_URL}/compose-email-ai`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${SUPABASE_CONFIG.anonKey}`,
          },
          body: JSON.stringify({
            messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
            originalEmail: originalEmail
              ? {
                  from: originalEmail.from,
                  subject: originalEmail.subject,
                  body: originalEmail.body,
                  attachments: originalEmail.attachments?.map(a => ({
                    name: a.name,
                    size: a.size,
                    contentType: a.contentType,
                  })),
                }
              : null,
            userName,
            signature: signatureText,
            needsSubject,
            customerContext,
            knowledgeBaseContent: knowledgeBaseContent.length > 0 ? knowledgeBaseContent : null,
            attachmentContent: attachmentContent.length > 0 ? attachmentContent : null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Fehler bei der KI-Anfrage");
      }

      if (!response.body) {
        throw new Error("Keine Antwort erhalten");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setGeneratedEmail(assistantContent);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Parse subject from response if present
      let emailBody = assistantContent;
      let extractedSubject: string | null = null;
      
      const subjectMatch = assistantContent.match(/^BETREFF:\s*(.+?)(\r?\n|\r)/i);
      if (subjectMatch) {
        extractedSubject = subjectMatch[1].trim();
        emailBody = assistantContent.slice(subjectMatch[0].length).trim();
        setSuggestedSubject(extractedSubject);
      }

      setMessages((prev) => [...prev, { role: "assistant", content: emailBody }]);
      setGeneratedEmail(emailBody);
    } catch (error) {
      console.error("AI compose error:", error);
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Ein Fehler ist aufgetreten",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    streamChat(input.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAccept = () => {
    if (generatedEmail) {
      onAccept(generatedEmail, suggestedSubject || undefined);
      onOpenChange(false);
    }
  };

  const handleToggleRecording = () => {
    toggleRecording();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-[70vh] sm:h-[80vh] flex flex-col p-0 [&>button]:hidden">
        <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Mit KI verfassen
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Context indicators */}
          {(customerContext || knowledgeBaseContent.length > 0 || loadingContext) && (
            <div className="px-4 py-2 border-b bg-muted/30 flex flex-wrap gap-2 text-xs">
              {loadingContext ? (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Kontext laden...
                </span>
              ) : (
                <>
                  {customerContext && (
                    <span className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      <Database className="h-3 w-3" />
                      Kunde: {customerContext.firstName} {customerContext.lastName}
                    </span>
                  )}
                  {knowledgeBaseContent.length > 0 && (
                    <span className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      <BookOpen className="h-3 w-3" />
                      {knowledgeBaseContent.length} Wissensbank-Einträge
                    </span>
                  )}
                  {originalEmail?.attachments && originalEmail.attachments.length > 0 && (
                    <span className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      <FileText className="h-3 w-3" />
                      {originalEmail.attachments.length} Anhänge
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Chat messages */}
          <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef}>
            <div className="space-y-4">
              {messages.length === 0 && !isLoading && (
                <div className="text-center text-muted-foreground py-8">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 text-primary/50" />
                  <p className="text-sm">
                    Beschreiben Sie, was in der E-Mail stehen soll.
                  </p>
                  {originalEmail && (
                    <p className="text-xs mt-2 text-muted-foreground/70">
                      Die ursprüngliche E-Mail wird berücksichtigt.
                    </p>
                  )}
                  {customerContext && (
                    <p className="text-xs mt-1 text-primary/70">
                      ✓ Kunde erkannt: {customerContext.firstName} {customerContext.lastName}
                    </p>
                  )}
                  {knowledgeBaseContent.length > 0 && (
                    <p className="text-xs mt-1 text-primary/70">
                      ✓ Wissensbank verfügbar ({knowledgeBaseContent.length} Einträge)
                    </p>
                  )}
                </div>
              )}

              {messages.map((message, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "flex gap-3",
                    message.role === "user" ? "justify-end" : "justify-start"
                  )}
                >
                  {message.role === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      "rounded-lg px-3 py-2 max-w-[80%] text-sm",
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    )}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                  {message.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <User className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && generatedEmail && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                  <div className="rounded-lg px-3 py-2 max-w-[80%] text-sm bg-muted">
                    <p className="whitespace-pre-wrap">{generatedEmail}</p>
                    <span className="inline-block w-2 h-4 bg-primary/50 animate-pulse ml-0.5" />
                  </div>
                </div>
              )}

              {isLoading && !generatedEmail && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Loader2 className="h-4 w-4 text-primary animate-spin" />
                  </div>
                  <div className="rounded-lg px-3 py-2 text-sm bg-muted text-muted-foreground">
                    E-Mail wird generiert...
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input area */}
          <div className="border-t p-4 space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="z.B. 'Schreibe eine höfliche Absage' oder 'Bedanke dich für das Angebot'"
                  className="min-h-[60px] resize-none text-sm"
                  disabled={isLoading || isRecording}
                />
                {isRecording && interimTranscript && (
                  <div className="absolute bottom-2 left-2 right-2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
                    {interimTranscript}...
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  onClick={handleToggleRecording}
                  disabled={isLoading || !isSpeechSupported}
                  size="icon"
                  variant={isRecording ? "destructive" : "outline"}
                  className={cn(
                    "h-[28px] w-[60px] shrink-0 transition-all",
                    isRecording && "animate-pulse ring-2 ring-destructive ring-offset-2"
                  )}
                  title={
                    !isSpeechSupported
                      ? "Spracherkennung nicht unterstützt"
                      : isRecording 
                        ? "Aufnahme stoppen" 
                        : "Spracheingabe"
                  }
                >
                  {isRecording ? (
                    <MicOff className="h-4 w-4" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading || isRecording}
                  size="icon"
                  className="h-[28px] w-[60px] shrink-0"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {generatedEmail && !isLoading && (
              <Button onClick={handleAccept} className="w-full gap-2">
                <Check className="h-4 w-4" />
                Übernehmen
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
